import api from './client';

export type LegalEntity = 'autoregium' | 'fomin';

export const LEGAL_ENTITY_LABELS: Record<LegalEntity, string> = {
  autoregium: 'ООО АвтоРегиум',
  fomin: 'ИП Фомин Д.А.',
};

export interface StockItem {
  id: number;
  legal_entity: LegalEntity;
  legal_entity_label: string;
  name: string;
  catalog_id?: number | null;
  unit: string;
  current_stock: number;
  is_marked: boolean;
  notes?: string | null;
  is_active: boolean;
  last_receipt_price?: number | null;        // себестоимость (из последнего прихода)
  markup_pct?: number | null;                // per-item наценка (null → default)
  effective_markup_pct?: number | null;      // фактическая наценка с учётом default
  selling_price?: number | null;             // цена продажи (cost * (1 + markup/100))
}

export interface StockItemPayload {
  legal_entity: LegalEntity;
  name: string;
  catalog_id?: number | null;
  unit?: string;
  current_stock?: number;
  is_marked?: boolean;
  markup_pct?: number | null;
  notes?: string | null;
  is_active?: boolean;
}

export interface StockSettings {
  default_markup_pct: number;
}

export const getStockSettings = (): Promise<StockSettings> =>
  api.get<StockSettings>('/stock/settings').then(r => r.data);

export const updateStockSettings = (data: StockSettings): Promise<StockSettings> =>
  api.put<StockSettings>('/stock/settings', data).then(r => r.data);

export interface StockItemListResponse {
  items: StockItem[];
  total: number;
  page: number;
  per_page: number;
}

export interface StockReceiptItem {
  id: number;
  stock_item_id: number;
  stock_item_name: string;
  quantity: number;
  price?: number | null;
  marking_code?: string | null;
}

export interface StockReceipt {
  id: number;
  legal_entity: LegalEntity;
  legal_entity_label: string;
  supplier_id?: number | null;
  supplier_name?: string | null;
  date: string;
  invoice_number?: string | null;
  notes?: string | null;
  created_by?: number | null;
  items: StockReceiptItem[];
}

export interface StockReceiptListResponse {
  items: StockReceipt[];
  total: number;
  page: number;
  per_page: number;
}

export interface ReceiptItemIn {
  stock_item_id?: number | null;
  name?: string;
  catalog_id?: number | null;
  unit?: string;
  is_marked?: boolean;
  quantity: number;
  price?: number | null;
  marking_code?: string | null;
}

export interface ReceiptCreatePayload {
  legal_entity: LegalEntity;
  supplier_id?: number | null;
  date?: string;
  invoice_number?: string | null;
  notes?: string | null;
  items: ReceiptItemIn[];
}

// ── Items
export const listStockItems = (params: {
  page?: number; per_page?: number; search?: string;
  legal_entity?: LegalEntity; include_inactive?: boolean; include_zero?: boolean;
} = {}): Promise<StockItemListResponse> =>
  api.get<StockItemListResponse>('/stock/items', { params }).then(r => r.data);

export const getStockItem = (id: number): Promise<StockItem> =>
  api.get<StockItem>(`/stock/items/${id}`).then(r => r.data);

export const updateStockItem = (id: number, data: Partial<StockItemPayload>): Promise<StockItem> =>
  api.put<StockItem>(`/stock/items/${id}`, data).then(r => r.data);

export const archiveStockItem = (id: number): Promise<void> =>
  api.delete(`/stock/items/${id}`).then(() => undefined);

// ── Receipts
export const listReceipts = (params: {
  page?: number; per_page?: number; legal_entity?: LegalEntity; supplier_id?: number;
} = {}): Promise<StockReceiptListResponse> =>
  api.get<StockReceiptListResponse>('/stock/receipts', { params }).then(r => r.data);

export const getReceipt = (id: number): Promise<StockReceipt> =>
  api.get<StockReceipt>(`/stock/receipts/${id}`).then(r => r.data);

export const createReceipt = (data: ReceiptCreatePayload): Promise<StockReceipt> =>
  api.post<StockReceipt>('/stock/receipts', data).then(r => r.data);

export const deleteReceipt = (id: number): Promise<void> =>
  api.delete(`/stock/receipts/${id}`).then(() => undefined);
